import { IconButton, Spinner, TableRow, type DataTableColumn } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import { checkpointSizeLabel } from "./trainingSessionDetailFormat";
import styles from "./TrainingSessionDetailPanels.module.css";

// 세션 run 이 남긴 파일 하나. Raw Checkpoint 와 부속 파일이 같은 필드와 같은 선택 키(run/path)를 쓴다.
export type SessionArtifactFile = {
  run_id: string;
  artifact_path: string;
  filename: string;
  size_bytes: number | null;
  updated_at: string | null;
};

export class SessionArtifactRow<T extends SessionArtifactFile> extends TableRow {
  readonly file: T;
  constructor(file: T) {
    super();
    this.file = file;
  }
  get updatedAt(): string | null {
    return this.file.updated_at;
  }
  get filename(): string {
    return this.file.filename;
  }
  get runId(): string {
    return this.file.run_id;
  }
  get sizeLabel(): string {
    return checkpointSizeLabel(this.file.size_bytes);
  }
  // 같은 이름의 파일이 run 마다 있다. 그래서 run 과 경로를 함께 쓴다.
  getRowId(): string {
    return JSON.stringify([this.file.run_id, this.file.artifact_path]);
  }
  canShow(): boolean {
    return true;
  }
  canSelect(): boolean {
    return false;
  }
}

// 파일(이름+경로) · Run ID · 크기. 두 표가 같은 폭과 같은 셀을 쓴다. 시각 컬럼과 동작 컬럼은 표마다 다르다.
export function sessionArtifactFileColumns<
  T extends SessionArtifactFile,
>(): readonly DataTableColumn<SessionArtifactRow<T>>[] {
  return [
    {
      key: "filename",
      header: "파일",
      width: "minmax(220px, 2fr)",
      render: ({ file }) => (
        <span className={styles.rawCell}>
          <span>{file.filename}</span>
          <HatTextTooltip text={file.artifact_path}>
            <span className={styles.rawPath}>{file.artifact_path}</span>
          </HatTextTooltip>
        </span>
      ),
    },
    {
      key: "runId",
      header: "Run ID",
      width: "minmax(110px, 0.7fr)",
      render: ({ file }) => (
        <HatTextTooltip text={file.run_id}>
          <span className={styles.rawPath}>{file.run_id}</span>
        </HatTextTooltip>
      ),
    },
    {
      key: "sizeLabel",
      header: "크기",
      width: "100px",
      align: "right",
      render: (row) => row.sizeLabel,
    },
  ];
}

// 한 번에 한 파일만 받는다. URL 발급이 끝나면 대기 표시를 해제한다. 발급 중에 section 이 사라져도
// (탭 이탈·창 닫기) 사라진 컴포넌트의 상태를 바꾸지 않는다.
export function useSessionArtifactDownload<T extends SessionArtifactFile>(
  download: (file: T) => Promise<void>,
): { pendingRowId: string | null; start: (row: SessionArtifactRow<T>) => void } {
  const [pendingRowId, setPendingRowId] = useState<string | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return {
    pendingRowId,
    start: (row) => {
      setPendingRowId(row.getRowId());
      void download(row.file).finally(() => {
        if (mounted.current) setPendingRowId(null);
      });
    },
  };
}

export function SessionArtifactDownloadButton({
  rowId,
  pendingRowId,
  onClick,
}: {
  rowId: string;
  pendingRowId: string | null;
  onClick: () => void;
}): ReactNode {
  return (
    <IconButton
      text="다운로드"
      size="sm"
      variant="surface"
      disabled={pendingRowId !== null}
      onClick={onClick}
    >
      {pendingRowId === rowId ? <Spinner size="sm" label="다운로드를 준비하고 있습니다." /> : null}
    </IconButton>
  );
}
